import { useCallback } from 'react'
import { useMutation } from 'react-query'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { postGatewayPayment } from 'services/easyPay'
import posthog from 'posthog-js'
import { rememberPaymentFlow } from 'utilities/analytics/paymentFlow'
import useFinishFlow, { FLOWS } from 'utilities/navigation/flow'
import useGatewayHandoff from 'utilities/hooks/useGatewayHandoff'

export default function useSetGatewayPayment() {
    const { state } = useLocation()
    const finishFlow = useFinishFlow(FLOWS.easyPay)
    const { locked, isLocked, lock, hold, release } = useGatewayHandoff()
    const { isLoading, mutate } = useMutation({
        mutationFn: (payload: IGatewayPaymentPayload) => {
            return postGatewayPayment(payload)
        },
        onSuccess: (result, variables) => {
            const items: any[] = (variables as any)?.paymentItems ?? []
            const total = items.reduce(
                (sum, item) =>
                    sum + Number(item?.itemAmount ?? 0) +
                    Number(item?.commission ?? 0),
                0
            )

            // Settled out of the wallet: the backend paid every item itself and
            // there is no bank to visit, so there is no `payment_link` to
            // follow and nothing for the callback page to finish. Redirecting
            // to an absent link is exactly what turned a settled payment into
            // «مشکلی پیش آمده» on the other wallet screens.
            if ((result as any)?.paid_from_wallet) {
                release()
                posthog.capture('payment_wallet_paid', {
                    entry: 'easy_pay',
                    amount: total,
                    items_count: items.length,
                })
                toast.success('پرداخت از کیف پول انجام شد')
                finishFlow('/funds')
                return
            }

            posthog.capture('payment_gateway_redirect', {
                flow_type: 'gateway_payment',
                gateway: variables?.gateway?.uniqueCode,
                use_wallet: (variables as any)?.use_wallet,
                items_count: variables?.paymentItems?.length,
                transaction_pk: (result as any)?.pk,
            })
            rememberPaymentFlow({
                flow_type: 'gateway_payment',
                amount: total,
                gateway: variables?.gateway?.uniqueCode ?? null,
                // A basket can span funds; naming one of them would be worse
                // than naming none.
                box_id: items.length === 1 ? items[0]?.box_id ?? null : null,
            })
            localStorage.setItem(
                'toBePaidTransactions',
                JSON.stringify({
                    type: 'group-all-payment',
                    data: state.toBePaidTransactions,
                    //@ts-ignore
                    pk: result?.pk,
                    //@ts-ignore
                    gateway: result?.gateway,
                })
            )
            // The button stays down while the browser is on its way to the
            // bank — see `useGatewayHandoff` for the ten-purchase afternoon.
            hold()
            //@ts-ignore
            window.location.replace(result?.payment_link)
        },
        onError: (error: any) => {
            release()
            // Surface the backend's specific message (e.g. a gateway that is not
            // enabled for this box returns {"error": "..."}). Fall back to the
            // generic toast when the body has no string message (e.g. serializer
            // errors come back as an object, or network errors have no body).
            const backendMessage = error?.response?.data?.error
            posthog.capture('payment_initiate_failed', {
                flow_type: 'gateway_payment',
                error_message:
                    typeof backendMessage === 'string' ? backendMessage : null,
                status: error?.response?.status,
            })
            toast.error(
                typeof backendMessage === 'string' && backendMessage
                    ? backendMessage
                    : 'مشکلی پیش آمده!'
            )
        },
    })
    const pay = useCallback(
        (payload: IGatewayPaymentPayload) => {
            // Refused by the ref, not by `isLoading`: a second tap in the same
            // tick lands before React has re-rendered `disabled`.
            if (isLocked()) return
            lock()
            mutate(payload)
        },
        [isLocked, lock, mutate]
    )
    return { mutate: pay, isLoading: isLoading || locked }
}
